<template>
    <div class="px-2 py-4 xl:px-10">
        <div class="max-w-8xl mx-auto w-full space-y-4">
            <!-- Page Header -->
            <div class="flex items-center justify-between">
                <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                    Permissions Management
                </h1>
                <button
                    @click="openModal"
                    class="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                    Add Permission
                </button>
            </div>

            <!-- Search and Filters -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <!-- Search -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Search
                        </label>
                        <input
                            v-model="searchQuery"
                            @input="debouncedSearch"
                            type="text"
                            placeholder="Search by permission name..."
                            class="w-full px-3 py-2\ border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    <!-- Group Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.group"
                            :options="groupOptions"
                            label="Group"
                            placeholder="All Groups"
                            option-label="label"
                            option-value="value"
                            @update:model-value="fetchPermissions"
                        />
                    </div>

                    <PageLengthSelector
                        v-model="permissionStore.perPage"
                        @update:model-value="handlePerPageChange"
                    />
                </div>
                <div class="flex justify-between items-center mt-4">
                    <div></div>
                    <button
                        @click="resetFilters"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    >
                        <RotateCcw class="w-4 h-4 mr-1" />
                        Reset Filters
                    </button>
                </div>
            </div>

            <!-- Loading State -->
            <div
                v-if="permissionStore.loading"
                class="flex justify-center items-center py-12"
            >
                <Loader2 class="animate-spin h-8 w-8 text-blue-600" />
            </div>

            <!-- Error State -->
            <div
                v-if="permissionStore.error"
                class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
            >
                <div class="flex">
                    <div class="flex-shrink-0">
                        <TriangleAlert class="h-5 w-5 text-red-400" />
                    </div>
                    <div class="ml-3">
                        <h3
                            class="text-sm font-medium text-red-800 dark:text-red-200"
                        >
                            Error
                        </h3>
                        <p class="mt-1 text-sm text-red-700 dark:text-red-300">
                            {{ permissionStore.error }}
                        </p>
                        <button
                            @click="permissionStore.clearError"
                            class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-500"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>

            <!-- Data Table -->
            <div
                v-if="!permissionStore.loading && !permissionStore.error"
                class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
            >
                <div
                    class="overflow-x-auto bg-gray-800 dark:bg-gray-900 shadow-md rounded-lg"
                >
                    <table
                        class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                        style="width: 100%"
                    >
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Permission Name
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Guard
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Group
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody
                            class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                        >
                            <tr
                                v-for="permission in permissionStore.permissions"
                                :key="permission.id"
                                class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div
                                        class="text-sm font-medium text-gray-900 dark:text-white"
                                    >
                                        {{ permission.name }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    <span
                                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                                    >
                                        {{ permission.guard_name }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        v-if="permission.group_name"
                                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                    >
                                        {{ permission.group_name }}
                                    </span>
                                    <span
                                        v-else
                                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                                    >
                                        No Group
                                    </span>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                                >
                                    <button
                                        @click="editPermission(permission)"
                                        title="Edit Permission"
                                        class="inline-flex items-center justify-center p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                    >
                                        <Pencil class="w-4 h-4" />
                                    </button>
                                    <button
                                        @click="confirmDelete(permission)"
                                        title="Delete Permission"
                                        class="inline-flex items-center justify-center p-2 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    >
                                        <Trash2 class="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Empty State -->
                <div
                    v-if="permissionStore.permissions.length === 0"
                    class="text-center py-12"
                >
                    <ShieldCheck class="mx-auto h-12 w-12 text-gray-400" />
                    <h3
                        class="mt-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                        No permissions found
                    </h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Get started by adding a new permission.
                    </p>
                    <div class="mt-6">
                        <button
                            @click="openModal"
                            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                            Add Permission
                        </button>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div
                v-if="
                    (permissionStore.meta?.total || 0) >
                    (permissionStore.meta?.per_page || 10)
                "
                class="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6"
            >
                <div class="flex-1 flex justify-between sm:hidden">
                    <button
                        @click="previousPage"
                        :disabled="permissionStore.meta?.current_page === 1"
                        class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        Previous
                    </button>
                    <button
                        @click="nextPage"
                        :disabled="
                            permissionStore.meta?.current_page ===
                            permissionStore.meta?.last_page
                        "
                        class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        Next
                    </button>
                </div>
                <div
                    class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between"
                >
                    <div>
                        <p class="text-sm text-gray-700 dark:text-gray-300">
                            Showing
                            <span class="font-medium">{{
                                permissionStore.meta?.from ??
                                permissionStore.meta?.current_page
                            }}</span>
                            to
                            <span class="font-medium">{{
                                permissionStore.meta?.to ??
                                permissionStore.meta?.per_page
                            }}</span>
                            of
                            <span class="font-medium">{{
                                permissionStore.meta?.total
                            }}</span>
                            results
                        </p>
                    </div>
                    <div>
                        <nav
                            class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                            aria-label="Pagination"
                        >
                            <button
                                @click="previousPage"
                                :disabled="
                                    permissionStore.meta?.current_page === 1
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                Previous
                            </button>
                            <button
                                v-for="page in visiblePages"
                                :key="page"
                                @click="
                                    typeof page === 'number'
                                        ? goToPage(page)
                                        : null
                                "
                                :class="[
                                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                    page === permissionStore.meta?.current_page
                                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-blue-900 dark:text-blue-300'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300',
                                    typeof page === 'string'
                                        ? 'cursor-not-allowed'
                                        : 'cursor-pointer',
                                ]"
                                :disabled="typeof page === 'string'"
                            >
                                {{ page }}
                            </button>
                            <button
                                @click="nextPage"
                                :disabled="
                                    permissionStore.meta?.current_page ===
                                    permissionStore.meta?.last_page
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                Next
                            </button>
                        </nav>
                    </div>
                </div>
            </div>

            <!-- Permission Modal -->
            <PermissionModal
                v-if="showModal"
                :show="showModal"
                :permission="selectedPermission || undefined"
                :is-editing="isEditing"
                @close="closeModal"
                @saved="handleSaved"
            />

            <!-- Delete Confirmation Modal -->
            <DeleteConfirmationModal
                v-if="showDeleteModal"
                :show="showDeleteModal"
                :title="'Delete Permission'"
                :message="
                    permissionToDelete
                        ? `Are you sure you want to delete the permission '${permissionToDelete.name}'? This action cannot be undone.`
                        : ''
                "
                @confirm="handleDelete"
                @close="closeDeleteModal"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { usePermissionStore } from "@/stores/permission";
import SearchableSelect from "@/components/SearchableSelect.vue";
import PermissionModal from "@/components/PermissionModal.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import PageLengthSelector from "@/components/PageLengthSelector.vue";
import NotificationService from "@/utils/notifications";
import type { Permission } from "@/api/role";
import {
    RotateCcw,
    Loader2,
    TriangleAlert,
    Pencil,
    Trash2,
    ShieldCheck,
} from "@lucide/vue";

const permissionStore = usePermissionStore();
// State
const searchQuery = ref("");
const filters = ref({
    group: "",
});
const permissionGroups = ref<string[]>([]);
const showModal = ref(false);
const showDeleteModal = ref(false);
const selectedPermission = ref<Permission | null>(null);
const isEditing = ref(false);
const permissionToDelete = ref<Permission | null>(null);

// Computed
const visiblePages = computed(() => {
    const current = permissionStore.meta?.current_page || 1;
    const last = permissionStore.meta?.last_page || 1;
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
        let i = Math.max(2, current - delta);
        i <= Math.min(last - 1, current + delta);
        i++
    ) {
        range.push(i);
    }

    if (current - delta > 2) {
        rangeWithDots.push(1, "...");
    } else {
        rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (current + delta < last - 1) {
        rangeWithDots.push("...", last);
    } else if (last > 1) {
        rangeWithDots.push(last);
    }

    return rangeWithDots;
});

const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.group !== "" ||
        permissionStore.page !== 1 ||
        permissionStore.perPage !== 10
    );
});

// Options for Group filter
const groupOptions = computed(() => {
    const options = [{ value: "", label: "All Groups" }];
    permissionGroups.value.forEach((group: string) => {
        options.push({ value: group, label: group });
    });
    return options;
});

// Methods
const handlePerPageChange = (value: number) => {
    permissionStore.perPage = value;
    permissionStore.page = 1;
    fetchPermissions();
};

const resetFilters = () => {
    searchQuery.value = "";
    filters.value.group = "";
    permissionStore.page = 1;
    permissionStore.perPage = 10;
    fetchPermissions();
};

const fetchPermissions = async () => {
    const searchFilters = {
        search: searchQuery.value,
        group: filters.value.group || undefined,
    };
    await permissionStore.fetchPermissions(searchFilters);
};

const fetchPermissionGroups = async () => {
    try {
        const response = await permissionStore.fetchPermissionGroups();
        permissionGroups.value = response.data || [];
    } catch (error) {
        console.error("Failed to fetch permission groups:", error);
    }
};

const debouncedSearch = useDebounce(fetchPermissions, 300);

const openModal = () => {
    selectedPermission.value = null;
    isEditing.value = false;
    showModal.value = true;
    // Clear any previous errors when opening modal
    permissionStore.clearError();
};

const editPermission = async (permission: Permission) => {
    // Fetch fresh permission data to ensure we have latest values
    try {
        const freshPermission = await permissionStore.fetchPermission(
            permission.id,
        );
        // Use setTimeout to ensure the modal state is updated after data is fetched
        setTimeout(() => {
            selectedPermission.value = freshPermission;
            isEditing.value = true;
            showModal.value = true;
        }, 0);
        // Clear any previous errors when opening modal
        permissionStore.clearError();
    } catch (error) {
        console.error("Failed to fetch permission for editing:", error);
        showNotification("Failed to load permission data", "error");
    }
};

const closeModal = () => {
    showModal.value = false;
    selectedPermission.value = null;
    isEditing.value = false;
};

const confirmDelete = (permission: Permission) => {
    permissionToDelete.value = permission;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    permissionToDelete.value = null;
};

const handleSaved = () => {
    closeModal();
    fetchPermissions();
    showNotification(
        isEditing.value
            ? "Permission updated successfully"
            : "Permission created successfully",
        "success",
    );
    permissionStore.clearError();
};

const handleDelete = async () => {
    try {
        if (permissionToDelete.value) {
            await permissionStore.deletePermission(permissionToDelete.value.id);
            showNotification("Permission deleted successfully", "success");
            closeDeleteModal();
        }
    } catch (error) {
        showNotification("Failed to delete permission", "error");
    }
};

const previousPage = () => {
    if ((permissionStore.meta?.current_page || 1) > 1) {
        permissionStore.setPage((permissionStore.meta?.current_page || 1) - 1);
    }
};

const nextPage = () => {
    if (
        (permissionStore.meta?.current_page || 1) <
        (permissionStore.meta?.last_page || 1)
    ) {
        permissionStore.setPage((permissionStore.meta?.current_page || 1) + 1);
    }
};

const goToPage = (page: number) => {
    permissionStore.setPage(page);
};

const showNotification = (message: string, type: "success" | "error") => {
    if (type === "success") {
        NotificationService.success(message);
    } else {
        NotificationService.error(message);
    }
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(() => {
    fetchPermissions();
    fetchPermissionGroups();
});
</script>
